import { ScrollArea } from '@cowbell/ui/core';
import { faArrowDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';
import {
  buildAssistantMessageMeta,
  type UIMessagePart,
} from '../../meta/build-assistant-message-meta';
import { MessageBubble } from '../MessageBubble/MessageBubble';
import type { TurnClock } from '../TurnStatus/turn-status';
import { useTurnClock } from '../TurnStatus/useTurnClock';
import { firstLiveIndex, groupTurns, type Turn } from './group-turns';
import {
  extractAttachments,
  extractReaction,
  extractText,
} from './message-text';
import styles from './MessageList.module.css';

export interface UIMessage {
  id: string;
  role: 'user' | 'assistant';
  parts?: UIMessagePart[];
}

export interface MessageListProps {
  readonly messages: UIMessage[];
  readonly isStreaming: boolean;
}

/** The user turn that produced the assistant message at `idx`: the nearest
 *  preceding user message. Threaded to action cards as the switch+replay
 *  fallback when a navigation card omits `replayQuery` (OMNI-442 / OMNI-580). */
function originatingQueryFor(
  messages: UIMessage[],
  idx: number
): string | undefined {
  for (let i = idx - 1; i >= 0; i--) {
    const m = messages[i];
    if (m?.role === 'user') {
      return extractText(m.parts);
    }
  }
  return undefined;
}

/** The user turn that answered the assistant message at `idx`: the first
 *  user message after it, stopping at the next assistant message so a
 *  reply to a *later* question is never misattributed to this one. Threaded
 *  to action cards so prompt cards (quick-reply) can tell an answered
 *  question from an open one — component-local selection state does not
 *  survive the remounts this list goes through mid-turn (OMNI-702). */
function followupUserTextFor(
  messages: UIMessage[],
  idx: number
): string | undefined {
  for (let i = idx + 1; i < messages.length; i++) {
    const m = messages[i];
    if (m?.role === 'user') {
      return extractText(m.parts);
    }
    if (m?.role === 'assistant') {
      return undefined;
    }
  }
  return undefined;
}

/** Memoized: the turn clock updates list state on every stream chunk, and
 *  without this every finished bubble would re-render its markdown each time.
 *  Only the streaming message's props change between chunks. */
const AssistantBubble = memo(function AssistantBubble({
  msg,
  isStreaming,
  isLastMessage,
  isFirstInGroup,
  originatingQuery,
  followupUserText,
  turnClock,
}: Readonly<{
  msg: UIMessage;
  isStreaming: boolean;
  isLastMessage: boolean;
  isFirstInGroup: boolean;
  originatingQuery: string | undefined;
  followupUserText: string | undefined;
  turnClock: TurnClock | null;
}>) {
  const text = useMemo(() => extractText(msg.parts), [msg.parts]);
  const meta = useMemo(
    () => buildAssistantMessageMeta(msg.parts ?? []),
    [msg.parts]
  );
  return (
    <MessageBubble
      role="assistant"
      text={text}
      meta={meta}
      isStreaming={isStreaming}
      messageId={msg.id}
      isLastMessage={isLastMessage}
      isFirstInGroup={isFirstInGroup}
      originatingQuery={originatingQuery}
      followupUserText={followupUserText}
      turnClock={turnClock}
    />
  );
});

/** Distance (px) from the end of the conversation within which the reader
 *  counts as caught up: no "Latest" pill. */
const CAUGHT_UP_PX = 48;

/** Long enough for a smooth scroll to the anchored question to finish. */
const ANCHOR_GLIDE_MS = 700;

/**
 * The conversation, organised as turns (a question and Moo's reply).
 *
 * Scrolling is anchored to the question, not chased to the bottom: sending
 * glides the new question to the top of the panel and reserves the rest of
 * the panel beneath it, so the answer writes downward into view and the
 * reader starts at its first line. Nothing scrolls while it streams. A
 * "Latest" pill appears when there is more below than fits, and while a long
 * answer is being read the question stays recalled in a slim bar at the top.
 */
export function MessageList({ messages, isStreaming }: MessageListProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const turnRefs = useRef(new Map<string, HTMLElement>());

  const turns = useMemo(() => groupTurns(messages), [messages]);
  const lastTurn = turns.at(-1);

  // Index of the last assistant message: the one that streams.
  let lastAssistantIdx = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]?.role === 'assistant') {
      lastAssistantIdx = i;
      break;
    }
  }

  // Replies stop being "live" the instant the user sends a new message — a
  // trailing user turn is appended synchronously, whereas the next assistant
  // reply doesn't render until the thinking step resolves. Gating
  // `isLastMessage` on this (not on the next assistant message landing)
  // freezes stale action cards the moment the user moves on, and keeps a card
  // live when one of its own buttons appends a result message (OMNI-1218).
  const liveFrom = firstLiveIndex(messages);

  const lastMessage = messages.at(-1);
  const showWaitingTail = isStreaming && lastMessage?.role !== 'assistant';

  // The list, not the bubble, owns the turn's clock: it spans the waiting tail
  // and the reply, and survives the bubble remounting mid-turn. AI SDK hands
  // back a fresh snapshot of the streaming message on every chunk, so a new
  // `lastMessage` reference is the "server sent something" signal.
  const turnClock = useTurnClock(isStreaming, lastMessage);

  const viewportHeight = useElementHeight(viewportRef);
  const [anchoredKey, setAnchoredKey] = useState<string | null>(null);
  const {
    pinned,
    showJump,
    update: updateScrollUi,
    holdJump,
  } = useScrollUi(viewportRef, endRef, turnRefs, turns);

  // A new turn: a question just asked anchors to the top. On first render, open
  // on the latest turn — at its start when it is taller than the panel, so a
  // long answer is read from its first line rather than its last.
  const lastTurnKey = lastTurn?.key ?? null;
  const seenLastTurnKey = useRef<string | null>(null);
  useLayoutEffect(() => {
    const isFirstRender = seenLastTurnKey.current === null;
    seenLastTurnKey.current = lastTurnKey;
    if (!lastTurn || lastTurnKey === null) {
      return;
    }
    if (lastTurn.assistants.length === 0 && lastTurn.user) {
      setAnchoredKey(lastTurnKey);
      return;
    }
    if (isFirstRender) {
      const restored = restoreAfterRebind(
        viewportRef.current,
        conversationSignature(messages)
      );
      if (!restored) {
        openOnLatestTurn(
          viewportRef.current,
          turnRefs.current.get(lastTurnKey)
        );
      }
    }
    // Only a change of last turn re-runs this; the turn object itself is a new
    // reference on every streamed chunk.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastTurnKey]);

  // Once the anchored turn has its reserved height, glide it to the top.
  useLayoutEffect(() => {
    if (anchoredKey === null) {
      return;
    }
    // The glide passes over content below the fold; the Latest pill would
    // flash up and away during it.
    holdJump(ANCHOR_GLIDE_MS);
    scrollTurnToTop(
      viewportRef.current,
      turnRefs.current.get(anchoredKey),
      'smooth'
    );
  }, [anchoredKey, holdJump]);

  // Remember where the reader was when this list goes away, so a remount of
  // the same conversation (see restoreAfterRebind) can put them back.
  const signatureRef = useRef('');
  signatureRef.current = conversationSignature(messages);
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    return () => {
      lastScroll = {
        top: viewport?.scrollTop ?? 0,
        signature: signatureRef.current,
        at: Date.now(),
      };
    };
  }, []);

  // Streaming changes heights without scrolling; the pill and pinned question
  // still need to know.
  const tailText = extractText(lastMessage?.parts);
  useEffect(() => {
    updateScrollUi();
  }, [tailText, isStreaming, messages.length, updateScrollUi]);

  const jumpToLatest = useCallback(() => {
    const viewport = viewportRef.current;
    const end = endRef.current;
    if (!viewport || !end) return;
    const top =
      end.getBoundingClientRect().bottom -
      viewport.getBoundingClientRect().top +
      viewport.scrollTop -
      viewport.clientHeight +
      CAUGHT_UP_PX;
    viewport.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }, []);

  const jumpToQuestion = useCallback((key: string) => {
    scrollTurnToTop(viewportRef.current, turnRefs.current.get(key), 'smooth');
  }, []);

  const listStyle = {
    '--moo-viewport-h': `${viewportHeight}px`,
  } as CSSProperties;

  return (
    <div className={styles.frame}>
      {/* The scrollbar shows whenever the conversation scrolls (wheel, keys,
          drag), not only on hover, so it is clear which pane is moving. */}
      <ScrollArea
        h="100%"
        viewportRef={viewportRef}
        type="scroll"
        scrollHideDelay={900}
      >
        <div className={styles.list} style={listStyle}>
          {turns.map((turn) => {
            const isLastTurn = turn.key === lastTurnKey;
            return (
              <div
                key={turn.key}
                role="group"
                ref={(el) => {
                  if (el) turnRefs.current.set(turn.key, el);
                  else turnRefs.current.delete(turn.key);
                }}
                className={styles.turn}
                data-anchored={isLastTurn && anchoredKey === turn.key}
                aria-label="Conversation turn"
              >
                {turn.user && (
                  <MessageBubble
                    role="user"
                    text={extractText(turn.user.msg.parts)}
                    reaction={extractReaction(turn.user.msg.parts)}
                    attachments={extractAttachments(turn.user.msg.parts)}
                  />
                )}
                {turn.assistants.map(({ msg, idx }, i) => {
                  // Only the in-flight reply streams. While waiting for it to
                  // start (trailing message is the user's), the prior assistant
                  // message is finalized — don't re-flag it and hide its
                  // action cards.
                  const isMsgStreaming =
                    isStreaming &&
                    idx === lastAssistantIdx &&
                    lastMessage?.role === 'assistant';
                  return (
                    <AssistantBubble
                      key={msg.id}
                      msg={msg}
                      isStreaming={isMsgStreaming}
                      isLastMessage={idx >= liveFrom}
                      isFirstInGroup={i === 0}
                      originatingQuery={originatingQueryFor(messages, idx)}
                      followupUserText={followupUserTextFor(messages, idx)}
                      turnClock={isMsgStreaming ? turnClock : null}
                    />
                  );
                })}
                {isLastTurn && showWaitingTail && (
                  <MessageBubble
                    role="assistant"
                    text=""
                    isStreaming
                    turnClock={turnClock}
                  />
                )}
                {isLastTurn && <div ref={endRef} aria-hidden />}
              </div>
            );
          })}
        </div>
      </ScrollArea>

      <PinnedQuestion pinned={pinned} onSelect={jumpToQuestion} />

      <button
        type="button"
        className={styles.jump}
        data-visible={showJump}
        aria-hidden={!showJump}
        tabIndex={showJump ? 0 : -1}
        onClick={jumpToLatest}
      >
        {isStreaming && <span className={styles.jumpLive} aria-hidden />}
        <FontAwesomeIcon icon={faArrowDown} />
        <span>Latest</span>
      </button>
    </div>
  );
}

/** The question whose answer is on screen, recalled at the top once the
 *  question itself has scrolled away. Kept rendered while hidden so it can
 *  animate out with its last text. */
function PinnedQuestion({
  pinned,
  onSelect,
}: Readonly<{
  pinned: PinnedState;
  onSelect: (key: string) => void;
}>) {
  const [last, setLast] = useState(pinned);
  if (pinned && pinned !== last) {
    setLast(pinned);
  }
  const shown = pinned ?? last;
  const isVisible = pinned !== null;

  return (
    <button
      type="button"
      className={styles.pinned}
      data-visible={isVisible}
      aria-hidden={!isVisible}
      tabIndex={isVisible ? 0 : -1}
      title={shown?.text}
      onClick={() => shown && onSelect(shown.key)}
    >
      <span className={styles.pinnedText}>{shown?.text}</span>
    </button>
  );
}

type PinnedState = { readonly key: string; readonly text: string } | null;

/** Tracks what the scroll position implies for the floating UI: which
 *  question to recall at the top, and whether there is unseen content below. */
function useScrollUi(
  viewportRef: RefObject<HTMLDivElement | null>,
  endRef: RefObject<HTMLDivElement | null>,
  turnRefs: RefObject<Map<string, HTMLElement>>,
  turns: readonly Turn[]
) {
  const [pinned, setPinned] = useState<PinnedState>(null);
  const [showJump, setShowJump] = useState(false);
  const turnsRef = useRef(turns);
  turnsRef.current = turns;
  const jumpHeldUntil = useRef(0);
  const releaseTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  const update = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const view = viewport.getBoundingClientRect();

    const end = endRef.current?.getBoundingClientRect();
    const isHeld = Date.now() < jumpHeldUntil.current;
    setShowJump(
      !isHeld && end ? end.bottom - view.bottom > CAUGHT_UP_PX : false
    );

    let next: PinnedState = null;
    for (const turn of turnsRef.current) {
      const section = turnRefs.current?.get(turn.key);
      if (!turn.user || !section) continue;
      const rect = section.getBoundingClientRect();
      const question = section.firstElementChild?.getBoundingClientRect();
      const coversTop = rect.top < view.top && rect.bottom > view.top + 64;
      if (coversTop && question && question.bottom < view.top) {
        next = { key: turn.key, text: extractText(turn.user.msg.parts) };
        break;
      }
    }
    setPinned((prev) =>
      prev?.key === next?.key && prev?.text === next?.text ? prev : next
    );
  }, [endRef, turnRefs, viewportRef]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    viewport.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      viewport.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
  }, [update, viewportRef]);

  /** Keeps the Latest pill down for `ms`, then re-reads the position. */
  const holdJump = useCallback(
    (ms: number) => {
      jumpHeldUntil.current = Date.now() + ms;
      setShowJump(false);
      clearTimeout(releaseTimer.current);
      releaseTimer.current = setTimeout(update, ms);
    },
    [update]
  );

  useEffect(() => () => clearTimeout(releaseTimer.current), []);

  return { pinned, showJump, update, holdJump };
}

/** The element's height in px, kept current as it resizes. */
function useElementHeight(ref: RefObject<HTMLElement | null>): number {
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    setHeight(el.clientHeight);
    const observer = new ResizeObserver(() => setHeight(el.clientHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return height;
}

/** Scrolls so the turn's top edge sits at the top of the panel. */
function scrollTurnToTop(
  viewport: HTMLDivElement | null,
  turn: HTMLElement | undefined,
  behavior: ScrollBehavior
) {
  if (!viewport || !turn || typeof viewport.scrollTo !== 'function') return;
  const top =
    turn.getBoundingClientRect().top -
    viewport.getBoundingClientRect().top +
    viewport.scrollTop -
    Number.parseFloat(getComputedStyle(turn).scrollMarginTop || '0');
  viewport.scrollTo({ top: Math.max(0, top), behavior });
}

/** Opening a conversation: land on the latest turn. If it fits, that is the
 *  bottom; if it is taller than the panel, its start. */
function openOnLatestTurn(
  viewport: HTMLDivElement | null,
  turn: HTMLElement | undefined
) {
  if (!viewport || typeof viewport.scrollTo !== 'function') return;
  if (turn && turn.getBoundingClientRect().height > viewport.clientHeight) {
    scrollTurnToTop(viewport, turn, 'auto');
    return;
  }
  viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'auto' });
}

/** Where the reader was when a list last unmounted. A fresh chat remounts the
 *  panel the moment its first reply binds a conversation id (MooChat keys the
 *  panel by it); without this the reader would be thrown back to the start
 *  of the answer they were reading. */
let lastScroll: { top: number; signature: string; at: number } | null = null;

/** How soon after an unmount a remount counts as the same view. */
const REBIND_WINDOW_MS = 2_000;

/** Identifies a conversation's content independent of message ids, which can
 *  change from client to server ids across the rebind. */
function conversationSignature(messages: readonly UIMessage[]): string {
  const first = messages.find((m) => m.role === 'user');
  return `${messages.length}:${first ? extractText(first.parts) : ''}`;
}

/** Puts the reader back where they were if this mount is the same
 *  conversation remounting right after it unmounted. */
function restoreAfterRebind(
  viewport: HTMLDivElement | null,
  signature: string
): boolean {
  const saved = lastScroll;
  lastScroll = null;
  if (
    !viewport ||
    !saved ||
    saved.signature !== signature ||
    Date.now() - saved.at > REBIND_WINDOW_MS ||
    typeof viewport.scrollTo !== 'function'
  ) {
    return false;
  }
  viewport.scrollTo({ top: saved.top, behavior: 'auto' });
  return true;
}
